// src/modules/financials/components/AdvanceAnalyticsSection.jsx
import React from 'react';
import { Card, Row, Col, Statistic, Empty } from 'antd';
import { Pie, Column } from '@ant-design/charts';
import { WalletOutlined } from '@ant-design/icons';

const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;

const AdvanceAnalyticsSection = ({ data = {}, loading = false }) => {
  const {
    totalLoanPayments = 0,
    totalCustomerPayouts = 0,
    summary = [],
    byPaymentMode = [],
  } = data;

  const totalPayments = totalLoanPayments + totalCustomerPayouts;

  // Pie — category breakdown
  const pieData = summary.filter(s => s.totalAmount > 0).map(s => ({
    category: s.category === 'LOAN_PAYMENT' ? 'Loan Payments' : 'Customer Payouts',
    amount: s.totalAmount,
  }));

  const pieConfig = {
    data: pieData,
    angleField: 'amount',
    colorField: 'category',
    radius: 0.85,
    innerRadius: 0.55,
    label: false,
    legend: { position: 'bottom', flipPage: false },
    color: ['#1890ff', '#52c41a'],
    tooltip: {
      items: [
        (d) => ({ name: d.category || '', value: inr(d.amount) }),
      ],
    },
    statistic: {
      title: { content: 'Total', style: { fontSize: 13 } },
      content: {
        content: inr(totalLoanPayments + totalCustomerPayouts),
        style: { fontSize: 16, fontWeight: 600 },
      },
    },
  };

  // Bar — by payment mode
  const barData = byPaymentMode.map(m => ({
    mode: m.mode || 'Other',
    amount: m.totalAmount,
    count: m.count,
  }));

  const barConfig = {
    data: barData,
    xField: 'mode',
    yField: 'amount',
    style: { radiusTopLeft: 4, radiusTopRight: 4, fill: '#722ed1' },
    label: false,
    tooltip: {
      items: [
        (d) => ({ name: d.mode || '', value: `${inr(d.amount)} (${d.count || 0} txns)` }),
      ],
    },
    axis: {
      y: { title: 'Amount (₹)' },
    },
  };

  // Column — total payments by category
  const totalPaymentsData = summary.filter((s) => s.totalAmount > 0).map((s) => ({
    category: s.category === 'LOAN_PAYMENT' ? 'Loan Payments' : 'Customer Payouts',
    amount: s.totalAmount,
  }));

  const totalPaymentsConfig = {
    data: totalPaymentsData,
    xField: 'category',
    yField: 'amount',
    colorField: 'category',
    color: ['#1890ff', '#52c41a'],
    columnStyle: { radius: [4, 4, 0, 0] },
    label: false,
    axis: { y: { title: 'Amount (₹)' }, x: { label: { autoHide: true } } },
    tooltip: {
      items: [
        (d) => ({ name: d.category, value: inr(d.amount) }),
      ],
    },
  };

  return (
    <Card
      title={<span><WalletOutlined style={{ color: '#722ed1', marginRight: 8 }} />Advance Payment Analytics</span>}
      style={{ borderRadius: 12, border: '1px solid #f0f0f0', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
      loading={loading}
    >
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12}>
          <Statistic
            title="Total Loan Payments"
            value={inr(totalLoanPayments)}
            valueStyle={{ color: '#1890ff', fontSize: 18 }}
          />
        </Col>
        <Col xs={12}>
          <Statistic
            title="Total Customer Payouts"
            value={inr(totalCustomerPayouts)}
            valueStyle={{ color: '#52c41a', fontSize: 18 }}
          />
        </Col>
      </Row>

      <Row gutter={[24, 16]}>
        <Col xs={24} md={12}>
          {pieData.length > 0 ? (
            <Pie {...pieConfig} height={240} />
          ) : (
            <Empty description="No advance data" />
          )}
        </Col>
        <Col xs={24} md={12}>
          {barData.length > 0 ? (
            <Column {...barConfig} height={240} />
          ) : (
            <Empty description="No payment mode data" />
          )}
        </Col>
      </Row>

      <Row gutter={[24, 16]} style={{ marginTop: 12 }}>
        <Col xs={24}>
          <Statistic
            title="Total Payments"
            value={inr(totalPayments)}
            valueStyle={{ color: '#722ed1', fontSize: 18 }}
          />
          {totalPaymentsData.length > 0 ? (
            <Column {...totalPaymentsConfig} height={220} />
          ) : (
            <Empty description="No payment totals" />
          )}
        </Col>
      </Row>
    </Card>
  );
};

export default AdvanceAnalyticsSection;
